import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import ModalBasic from '../components/ModalBasic';
import { TrashIcon } from '@heroicons/react/20/solid';
import { useUser } from '../userContext';
import { notifySuccess } from '../utils/notify';
import { verifyCampaignOwnership, checkOperationRateLimit, recordOperation } from '../utils/campaignSecurity';

export default function DeleteCampaign({ campaignId, inline = false }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { businessProfile } = useUser();

  const handleDelete = async () => {
    if (!businessProfile?.id) {
      setError('Authentication required. Please refresh and try again.');
      return;
    }

    if (!campaignId) {
      setError('Invalid campaign ID.');
      return;
    }

    // SECURITY: Check rate limiting first
    const rateCheck = checkOperationRateLimit('campaign_delete', businessProfile.id);
    if (!rateCheck.allowed) {
      setError(rateCheck.message);
      return;
    }

    setIsDeleting(true);
    setError('');
    
    try {
      // SECURITY: Verify ownership using secure utility
      const authCheck = await verifyCampaignOwnership(campaignId, businessProfile.id);
      if (!authCheck.authorized) {
        setError(authCheck.error);
        setIsDeleting(false);
        return;
      }

      // Only delete if user owns the campaign
      const docRef = doc(db, "campaign", campaignId);
      await deleteDoc(docRef);
      
      // Record successful operation for rate limiting
      recordOperation('campaign_delete', businessProfile.id);

      notifySuccess('Campaign deleted');
      setModalOpen(false);
      navigate("/studio/dashboard");
    } catch (error) {
      console.error("Error deleting campaign:", error);
      setError('Failed to delete campaign. Please try again.');
      setIsDeleting(false);
    }
  };

  // Helper function to open modal with delay
  const openDeleteModal = () => {
    // Add 300ms delay before showing modal
    setTimeout(() => {
      setModalOpen(true);
    }, 300);
  };

  const buttonClasses = inline
    ? "btn-admin-outline btn-admin-sm inline-flex items-center gap-2 text-red-400 border-red-400/30 hover:bg-red-500/10 disabled:opacity-50"
    : "w-full btn-admin-outline btn-admin-sm flex items-center gap-2 text-red-400 border-red-400/30 hover:bg-red-500/10 disabled:opacity-50";

  return (
    <>
      <div className={inline ? '' : 'mt-0'}>
        <button
          onClick={openDeleteModal}
          className={buttonClasses}
        >
          <TrashIcon className="w-4 h-4" />
          Delete campaign
        </button>
      </div>

      <ModalBasic
        id="delete-campaign-modal"
        title="Delete campaign"
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
      >
          <div className="p-6 gch-bg-card" style={{fontFamily: '"Inter", sans-serif'}}>
            <div className="space-y-6">
              <p className="text-sm gch-text-primary">
                Are you sure you want to delete this campaign? This action cannot be undone.
              </p>
            
            {/* Error display */}
              {error && (
                <div className="p-3 rounded-lg bg-white/5 border border-[var(--admin-border)]">
                  <div className="flex items-start gap-2">
                    <span className="text-red-600">⚠️</span>
                    <span className="text-sm font-semibold text-red-700">{error}</span>
                  </div>
                </div>
              )}
            
            <div className="flex flex-wrap justify-end space-x-2">
              <button
                className="px-4 py-2.5 text-sm font-semibold transition-all duration-200 rounded-lg gch-bg-card hover:bg-white/5 border border-[var(--admin-border)] gch-text-primary"
                onClick={() => setModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className={`px-4 py-2.5 text-sm font-semibold transition-all duration-200 rounded-lg text-white ${
                  isDeleting 
                    ? 'bg-white/10 border border-[var(--admin-border)] cursor-not-allowed' 
                    : 'bg-red-600 hover:bg-white/50 border border-red-500'
                }`}
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      </ModalBasic>
    </>
  );
}
